/**
 * SkinClarity Wishlist System - Best-in-Class Implementation
 * 
 * Features:
 * - Unified localStorage key (migrates from old keys)
 * - Uses product handles for reliability
 * - Cross-tab synchronization
 * - Smooth animations and feedback
 * - Mobile-optimized UX
 * - Guest user support with localStorage
 */

class SkinClarityWishlist {
  constructor() {
    // Primary storage key - all data consolidated here
    this.storageKey = 'skc_wishlist';
    
    // Legacy keys to migrate from
    this.legacyKeys = ['skincare_wishlist', 'premium_wishlist'];
    
    // Migrate any old data first
    this.migrateFromLegacyKeys();
    
    // Initialize
    this.init();
  }

  /**
   * Migrate data from old localStorage keys to the new unified key
   */
  migrateFromLegacyKeys() {
    try {
      const currentWishlist = this.getWishlist();
      let migrated = false;
      
      this.legacyKeys.forEach(key => {
        const legacyData = localStorage.getItem(key);
        if (legacyData) {
          try {
            const legacyItems = JSON.parse(legacyData);
            if (Array.isArray(legacyItems) && legacyItems.length > 0) {
              // Merge legacy items (could be IDs or handles)
              legacyItems.forEach(item => {
                const itemStr = String(item);
                if (itemStr && !currentWishlist.includes(itemStr)) {
                  currentWishlist.push(itemStr);
                  migrated = true;
                }
              });
            }
          } catch (e) {
            console.warn(`Failed to parse legacy wishlist from ${key}`, e);
          }
          
          // Remove legacy key after migration
          localStorage.removeItem(key);
        }
      });
      
      if (migrated) {
        this.saveWishlistSilent(currentWishlist);
        console.log('Wishlist migrated to unified storage');
      }
    } catch (e) {
      console.error('Wishlist migration failed:', e);
    }
  }

  init() {
    // Plan 08-01: Click handler must be live IMMEDIATELY (user can tap a heart
    // before idle fires). DOM-walking refresh (updateAllButtons + updateCounter)
    // is purely below-the-fold and is safe to idle-defer for INP/TBT win.
    document.addEventListener('click', this.handleClick.bind(this));

    var self = this;
    var runIdle = (typeof window !== 'undefined' && window.scRunOnIdle) ||
                  function(cb) { setTimeout(cb, 1500); };
    runIdle(function() {
      self.updateAllButtons();
      self.updateCounter();
    }, 2000);

    // Listen for wishlist updates from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.updateAllButtons();
        this.updateCounter();
      }
    });

    // Listen for custom wishlist events
    window.addEventListener('wishlist:updated', () => {
      this.updateAllButtons();
      this.updateCounter();
    });

    // Re-check on Shopify section reload (theme editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        this.updateAllButtons();
        this.updateCounter();
      });
    }
  }

  /**
   * Get wishlist from localStorage
   * @returns {Array} Array of product handles
   */
  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Save wishlist to localStorage (silent, no events)
   */
  saveWishlistSilent(wishlist) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }

  /**
   * Save wishlist and dispatch events
   */
  saveWishlist(wishlist) {
    this.saveWishlistSilent(wishlist);
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('wishlist:updated', { 
      detail: { wishlist, count: wishlist.length } 
    }));
    
    // Also dispatch storage event for same-tab listeners
    window.dispatchEvent(new Event('storage'));
  }

  /**
   * Check if a product is in the wishlist
   * Supports both handle and ID (for backwards compatibility)
   */
  isInWishlist(productIdentifier) {
    if (!productIdentifier) return false;
    const identifier = String(productIdentifier);
    const wishlist = this.getWishlist();
    return wishlist.includes(identifier);
  }

  /**
   * Toggle a product in the wishlist
   */
  toggle(productIdentifier) {
    if (!productIdentifier) return false;
    const identifier = String(productIdentifier);

    let wishlist = this.getWishlist();
    const index = wishlist.indexOf(identifier);
    let added = false;

    if (index > -1) {
      // Remove from wishlist
      wishlist.splice(index, 1);
      this.showNotification('Verwijderd uit favorieten', 'remove');
    } else {
      // Add to wishlist
      wishlist.push(identifier);
      this.showNotification('Toegevoegd aan favorieten', 'add');
      added = true;
    }

    this.saveWishlist(wishlist);
    this.updateAllButtons();
    this.updateCounter();

    return added;
  }

  /**
   * Add a product to the wishlist
   */
  add(productIdentifier) {
    if (!productIdentifier) return;
    const identifier = String(productIdentifier);

    let wishlist = this.getWishlist();
    if (!wishlist.includes(identifier)) {
      wishlist.push(identifier);
      this.saveWishlist(wishlist);
      this.showNotification('Toegevoegd aan favorieten', 'add');
    }

    this.updateAllButtons();
    this.updateCounter();
  }

  /**
   * Remove a product from the wishlist
   */
  remove(productIdentifier) {
    if (!productIdentifier) return;
    const identifier = String(productIdentifier);

    let wishlist = this.getWishlist();
    const index = wishlist.indexOf(identifier);

    if (index > -1) {
      wishlist.splice(index, 1);
      this.saveWishlist(wishlist);
      this.showNotification('Verwijderd uit favorieten', 'remove');
    }

    this.updateAllButtons();
    this.updateCounter();
  }

  /**
   * Clear all items from the wishlist
   */
  clear() {
    this.saveWishlist([]);
    this.updateAllButtons();
    this.updateCounter();
    this.showNotification('Alle favorieten gewist', 'remove');
  }

  /**
   * Get the count of items in the wishlist
   */
  getCount() {
    return this.getWishlist().length;
  }

  /**
   * Handle click events on wishlist buttons
   */
  handleClick(event) {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    // Get product identifier (prefer handle, fallback to ID)
    const productHandle = button.dataset.productHandle;
    const productId = button.dataset.productId;
    const identifier = productHandle || productId;
    
    if (identifier) {
      this.toggle(identifier);
      
      // Add animation class
      button.classList.add('wishlist-pulse');
      setTimeout(() => button.classList.remove('wishlist-pulse'), 400);
    }
  }

  /**
   * Update all wishlist buttons to reflect current state
   */
  updateAllButtons() {
    const wishlist = this.getWishlist();
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');

    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      const productId = button.dataset.productId;
      const identifier = productHandle || productId;
      const isActive = identifier && wishlist.includes(String(identifier));
      
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive);
      
      // Update SVG fill
      const svg = button.querySelector('svg');
      if (svg) {
        const path = svg.querySelector('path');
        if (path) {
          path.setAttribute('fill', isActive ? 'var(--color-accent, #D4AF37)' : 'none');
        }
      }
      
      // Update tooltip/label
      const label = button.querySelector('.wishlist-label, .visually-hidden');
      if (label) {
        label.textContent = isActive ? 'Verwijder uit favorieten' : 'Voeg toe aan favorieten';
      }
      
      // Update aria-label
      button.setAttribute('aria-label', isActive ? 'Verwijder uit favorieten' : 'Voeg toe aan favorieten');
    });
  }

  /**
   * Update all wishlist counters
   */
  updateCounter() {
    const count = this.getCount();
    const counters = document.querySelectorAll('[data-wishlist-count], .skc-wishlist-count, .wishlist-count');

    counters.forEach(counter => {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
      counter.classList.toggle('has-items', count > 0);
    });
  }

  /**
   * Show a notification toast
   * DISABLED on wishlist page to prevent flickering
   */
  showNotification(message, type = 'add') {
    // Don't show notifications on the wishlist page (prevents flicker)
    if (window.location.pathname.includes('/pages/favorieten') || 
        window.location.pathname.includes('/pages/wishlist') ||
        document.querySelector('.skc-wishlist-standalone')) {
      return;
    }
    
    // Remove existing notification
    const existing = document.querySelector('.skc-wishlist-notification');
    if (existing) {
      existing.remove();
    }

    const notification = document.createElement('div');
    notification.className = 'skc-wishlist-notification';
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');
    notification.innerHTML = `
      <div class="skc-wishlist-notification__icon">
        ${type === 'add' ? `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--color-accent, #D4AF37)" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M20.8401 4.60999C20.3294 4.09946 19.7229 3.69352 19.0555 3.41696C18.388 3.1404 17.6726 2.99805 16.9501 2.99805C16.2276 2.99805 15.5122 3.1404 14.8448 3.41696C14.1773 3.69352 13.5709 4.09946 13.0601 4.60999L12.0001 5.66999L10.9401 4.60999C9.90843 3.57838 8.50915 2.99874 7.05012 2.99874C5.59109 2.99874 4.19181 3.57838 3.16012 4.60999C2.12843 5.6416 1.54878 7.04099 1.54878 8.49999C1.54878 9.95899 2.12843 11.3584 3.16012 12.39L12.0001 21.23L20.8401 12.39C21.3506 11.8792 21.7565 11.2728 22.0331 10.6053C22.3096 9.93986 22.452 9.22446 22.452 8.50199C22.452 7.77953 22.3096 7.06413 22.0331 6.39663C21.7565 5.72913 21.3506 5.12271 20.8401 4.60999Z" stroke="var(--color-accent, #D4AF37)" stroke-width="2"/>
          </svg>
        ` : `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M20.8401 4.60999C20.3294 4.09946 19.7229 3.69352 19.0555 3.41696C18.388 3.1404 17.6726 2.99805 16.9501 2.99805C16.2276 2.99805 15.5122 3.1404 14.8448 3.41696C14.1773 3.69352 13.5709 4.09946 13.0601 4.60999L12.0001 5.66999L10.9401 4.60999C9.90843 3.57838 8.50915 2.99874 7.05012 2.99874C5.59109 2.99874 4.19181 3.57838 3.16012 4.60999C2.12843 5.6416 1.54878 7.04099 1.54878 8.49999C1.54878 9.95899 2.12843 11.3584 3.16012 12.39L12.0001 21.23L20.8401 12.39C21.3506 11.8792 21.7565 11.2728 22.0331 10.6053C22.3096 9.93986 22.452 9.22446 22.452 8.50199C22.452 7.77953 22.3096 7.06413 22.0331 6.39663C21.7565 5.72913 21.3506 5.12271 20.8401 4.60999Z" stroke="currentColor" stroke-width="2"/>
          </svg>
        `}
      </div>
      <span class="skc-wishlist-notification__message">${message}</span>
    `;

    document.body.appendChild(notification);

    // Trigger animation
    requestAnimationFrame(() => {
      notification.classList.add('is-visible');
    });

    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  }
}

// Initialize singleton
window.skcWishlist = new SkinClarityWishlist();

// Global helper functions for backwards compatibility
window.addToWishlist = (identifier) => window.skcWishlist.add(identifier);
window.removeFromWishlist = (identifier) => window.skcWishlist.remove(identifier);
window.toggleWishlist = (identifier) => window.skcWishlist.toggle(identifier);
window.isInWishlist = (identifier) => window.skcWishlist.isInWishlist(identifier);
window.getWishlist = () => window.skcWishlist.getWishlist();
window.getWishlistCount = () => window.skcWishlist.getCount();
window.clearWishlist = () => window.skcWishlist.clear();

// Backwards compatible function used by some sections
window.updateWishlistHeaderIcons = () => window.skcWishlist.updateCounter();
